import React, { useState } from 'react';
import { BANKS } from '../constants';

const Employees = ({
    employees,
    leaveRecords,
    salaryPayments,
    onAddEmployee,
    onUpdateEmployee,
    onDeleteEmployee,
    onAddLeave,
    onRecordSalary,
    formatCurrency
}) => {
    const [view, setView] = useState('list'); // list, profile, addEmployee, addLeave
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        position: '',
        joinDate: '',
        monthlySalary: '',
        email: '',
        phone: '',
        annualLeaveTotal: 14,
        sickLeaveTotal: 6
    });
    const [leaveFormData, setLeaveFormData] = useState({
        employeeId: '',
        leaveType: 'annual',
        startDate: '',
        endDate: '',
        reason: ''
    });
    const [salaryFormData, setSalaryFormData] = useState({
        employeeId: '',
        month: '',
        bankId: 'alfalah',
        alreadyRecorded: false
    });

    // Calculate leave usage for an employee
    const getLeaveStats = (employeeId) => {
        const employee = employees.find(e => e.id === employeeId);
        if (!employee) return { annualUsed: 0, sickUsed: 0, annualTotal: 14, sickTotal: 6 };

        const employeeLeaves = leaveRecords.filter(l => l.employeeId === employeeId);
        const annualUsed = employeeLeaves
            .filter(l => l.leaveType === 'annual')
            .reduce((sum, l) => sum + (l.days || 0), 0);
        const sickUsed = employeeLeaves
            .filter(l => l.leaveType === 'sick')
            .reduce((sum, l) => sum + (l.days || 0), 0);

        return {
            annualUsed,
            sickUsed,
            annualTotal: employee.annualLeaveTotal || 14,
            sickTotal: employee.sickLeaveTotal || 6,
            annualRemaining: (employee.annualLeaveTotal || 14) - annualUsed,
            sickRemaining: (employee.sickLeaveTotal || 6) - sickUsed
        };
    };

    // Calculate days between dates
    const calculateDays = (start, end) => {
        if (!start || !end) return 0;
        const startDate = new Date(start);
        const endDate = new Date(end);
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end
        return diffDays;
    };

    const handleAddEmployee = async (e) => {
        e.preventDefault();
        await onAddEmployee(formData);
        setFormData({
            name: '',
            position: '',
            joinDate: '',
            monthlySalary: '',
            email: '',
            phone: '',
            annualLeaveTotal: 14,
            sickLeaveTotal: 6
        });
        setView('list');
    };

    const handleAddLeave = async (e) => {
        e.preventDefault();
        const days = calculateDays(leaveFormData.startDate, leaveFormData.endDate);
        const employee = employees.find(e => e.id === leaveFormData.employeeId);

        await onAddLeave({
            ...leaveFormData,
            employeeName: employee.name,
            days,
            status: 'approved'
        });

        setLeaveFormData({
            employeeId: '',
            leaveType: 'annual',
            startDate: '',
            endDate: '',
            reason: ''
        });
        setView('list');
    };

    const handleRecordSalary = async (e) => {
        e.preventDefault();
        const employee = employees.find(e => e.id === salaryFormData.employeeId);

        await onRecordSalary({
            employeeId: employee.id,
            employeeName: employee.name,
            month: salaryFormData.month,
            amount: employee.monthlySalary,
            bankId: salaryFormData.bankId,
            alreadyRecorded: salaryFormData.alreadyRecorded
        });

        setSalaryFormData({
            employeeId: '',
            month: '',
            bankId: 'alfalah',
            alreadyRecorded: false
        });
        setView('list');
    };

    const handleDeleteEmployee = async (employee) => {
        if (window.confirm(`Are you sure you want to delete ${employee.name}? This action cannot be undone.`)) {
            await onDeleteEmployee(employee.id);
            if (selectedEmployee?.id === employee.id) {
                setSelectedEmployee(null);
                setView('list');
            }
        }
    };

    const handleEditEmployee = (employee) => {
        setSelectedEmployee(employee);
        setFormData({
            name: employee.name,
            position: employee.position,
            joinDate: employee.joinDate,
            monthlySalary: employee.monthlySalary,
            email: employee.email || '',
            phone: employee.phone || '',
            annualLeaveTotal: employee.annualLeaveTotal || 14,
            sickLeaveTotal: employee.sickLeaveTotal || 6
        });
        setView('editEmployee');
    };

    const handleUpdateEmployee = async (e) => {
        e.preventDefault();
        await onUpdateEmployee(selectedEmployee.id, formData);
        setFormData({
            name: '',
            position: '',
            joinDate: '',
            monthlySalary: '',
            email: '',
            phone: '',
            annualLeaveTotal: 14,
            sickLeaveTotal: 6
        });
        setSelectedEmployee(null);
        setView('list');
    };

    const totalMonthlyPayroll = employees.reduce((sum, emp) => sum + (Number(emp.monthlySalary) || 0), 0);

    // Employee List View
    if (view === 'list') {
        return (
            <div className="fade-in">
                <div className="flex justify-between items-center mb-6 flex-mobile-col">
                    <h2 className="text-2xl font-bold text-yellow">👥 Employee Management</h2>
                    <button onClick={() => setView('addEmployee')} className="btn btn-primary">
                        ➕ Add Employee
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="stat-card success">
                        <p className="text-sm text-gray mb-1">Total Employees</p>
                        <p className="text-2xl font-bold text-green">{employees.length}</p>
                    </div>
                    <div className="stat-card warning">
                        <p className="text-sm text-gray mb-1">Monthly Payroll</p>
                        <p className="text-2xl font-bold text-orange">{formatCurrency(totalMonthlyPayroll)}</p>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3 mb-6">
                    <button onClick={() => setView('addLeave')} className="btn btn-secondary" style={{ flex: 1 }}>
                        📅 Record Leave
                    </button>
                    <button onClick={() => setView('paySalary')} className="btn btn-secondary" style={{ flex: 1 }}>
                        💰 Pay Salary
                    </button>
                </div>

                {/* Employee List */}
                {employees.length === 0 ? (
                    <div className="card text-center py-8">
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                        <p className="text-gray">No employees yet</p>
                        <p className="text-muted text-sm">Add your first employee to get started</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {employees.map(employee => {
                            const leaveStats = getLeaveStats(employee.id);
                            const currentMonth = new Date().toISOString().slice(0, 7);
                            const salaryPaid = salaryPayments.find(p => p.employeeId === employee.id && p.month === currentMonth);

                            return (
                                <div key={employee.id} className="card">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h3 className="text-xl font-bold text-yellow">{employee.name}</h3>
                                            <p className="text-gray">{employee.position}</p>
                                            <p className="text-sm text-muted">Joined: {new Date(employee.joinDate).toLocaleDateString()}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-bold text-green">{formatCurrency(employee.monthlySalary)}</p>
                                            <p className="text-xs text-muted">per month</p>
                                        </div>
                                    </div>

                                    {/* Leave Stats */}
                                    <div className="grid grid-cols-2 gap-3 mb-3">
                                        <div style={{
                                            background: 'rgba(59,130,246,0.1)',
                                            padding: '0.5rem',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(59,130,246,0.3)'
                                        }}>
                                            <p className="text-xs text-gray mb-1">📅 Annual Leave</p>
                                            <p className="text-sm font-bold">
                                                {leaveStats.annualRemaining}/{leaveStats.annualTotal} left
                                            </p>
                                        </div>
                                        <div style={{
                                            background: 'rgba(239,68,68,0.1)',
                                            padding: '0.5rem',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(239,68,68,0.3)'
                                        }}>
                                            <p className="text-xs text-gray mb-1">🏥 Sick Leave</p>
                                            <p className="text-sm font-bold">
                                                {leaveStats.sickRemaining}/{leaveStats.sickTotal} left
                                            </p>
                                        </div>
                                    </div>

                                    {/* Salary Status */}
                                    {salaryPaid ? (
                                        <div style={{
                                            background: 'rgba(16,185,129,0.1)',
                                            padding: '0.5rem',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(16,185,129,0.3)',
                                            marginBottom: '0.75rem'
                                        }}>
                                            <p className="text-xs text-green">✅ Salary paid for {currentMonth}</p>
                                        </div>
                                    ) : (
                                        <div style={{
                                            background: 'rgba(245,158,11,0.1)',
                                            padding: '0.5rem',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(245,158,11,0.3)',
                                            marginBottom: '0.75rem'
                                        }}>
                                            <p className="text-xs text-orange">⏳ Salary pending for {currentMonth}</p>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setSelectedEmployee(employee);
                                                setView('profile');
                                            }}
                                            className="btn btn-secondary"
                                            style={{ flex: 1, padding: '0.5rem', fontSize: '0.875rem' }}
                                        >
                                            👁️ View
                                        </button>
                                        <button
                                            onClick={() => {
                                                setLeaveFormData({ ...leaveFormData, employeeId: employee.id });
                                                setView('addLeave');
                                            }}
                                            className="btn btn-secondary"
                                            style={{ flex: 1, padding: '0.5rem', fontSize: '0.875rem' }}
                                        >
                                            📅 Leave
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSalaryFormData({ ...salaryFormData, employeeId: employee.id, month: currentMonth });
                                                setView('paySalary');
                                            }}
                                            className="btn btn-primary"
                                            style={{ flex: 1, padding: '0.5rem', fontSize: '0.875rem' }}
                                            disabled={!!salaryPaid}
                                        >
                                            💰 Pay
                                        </button>
                                        <button
                                            onClick={() => handleDeleteEmployee(employee)}
                                            className="btn btn-danger"
                                            style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                            title="Delete employee"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // Add Employee Form
    if (view === 'addEmployee') {
        return (
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <button onClick={() => setView('list')} className="btn btn-secondary mb-4">
                    ← Back to List
                </button>
                <h2 className="text-2xl font-bold mb-6">➕ Add New Employee</h2>

                <form onSubmit={handleAddEmployee}>
                    <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Position/Role *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                            placeholder="e.g., Developer, Designer"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Join Date *</label>
                        <input
                            type="date"
                            className="form-input"
                            value={formData.joinDate}
                            onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Monthly Salary (PKR) *</label>
                        <input
                            type="number"
                            className="form-input"
                            value={formData.monthlySalary}
                            onChange={(e) => setFormData({ ...formData, monthlySalary: e.target.value })}
                            placeholder="50000"
                            min="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            className="form-input"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="employee@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Phone</label>
                        <input
                            type="tel"
                            className="form-input"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="+92 300 1234567"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                            <label className="form-label">Annual Leave (days)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData.annualLeaveTotal}
                                onChange={(e) => setFormData({ ...formData, annualLeaveTotal: Number(e.target.value) })}
                                min="0"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Sick Leave (days)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData.sickLeaveTotal}
                                onChange={(e) => setFormData({ ...formData, sickLeaveTotal: Number(e.target.value) })}
                                min="0"
                            />
                        </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                            ✅ Add Employee
                        </button>
                        <button type="button" onClick={() => setView('list')} className="btn btn-secondary">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    // Edit Employee Form
    if (view === 'editEmployee' && selectedEmployee) {
        return (
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <button onClick={() => setView('profile')} className="btn btn-secondary mb-4">
                    ← Back to Profile
                </button>
                <h2 className="text-2xl font-bold mb-6">✏️ Edit Employee</h2>

                <form onSubmit={handleUpdateEmployee}>
                    <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Position/Role *</label>
                        <input
                            type="text"
                            className="form-input"
                            value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                            placeholder="e.g., Developer, Designer"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Join Date *</label>
                        <input
                            type="date"
                            className="form-input"
                            value={formData.joinDate}
                            onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Monthly Salary (PKR) *</label>
                        <input
                            type="number"
                            className="form-input"
                            value={formData.monthlySalary}
                            onChange={(e) => setFormData({ ...formData, monthlySalary: e.target.value })}
                            placeholder="50000"
                            min="0"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Email</label>
                        <input
                            type="email"
                            className="form-input"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            placeholder="employee@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Phone</label>
                        <input
                            type="tel"
                            className="form-input"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="+92 300 1234567"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                            <label className="form-label">Annual Leave (days)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData.annualLeaveTotal}
                                onChange={(e) => setFormData({ ...formData, annualLeaveTotal: Number(e.target.value) })}
                                min="0"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Sick Leave (days)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData.sickLeaveTotal}
                                onChange={(e) => setFormData({ ...formData, sickLeaveTotal: Number(e.target.value) })}
                                min="0"
                            />
                        </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                            ✅ Update Employee
                        </button>
                        <button type="button" onClick={() => setView('profile')} className="btn btn-secondary">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    // Add Leave Form
    if (view === 'addLeave') {
        const days = calculateDays(leaveFormData.startDate, leaveFormData.endDate);
        const employee = employees.find(e => e.id === leaveFormData.employeeId);
        const leaveStats = employee ? getLeaveStats(employee.id) : null;

        return (
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <button onClick={() => setView('list')} className="btn btn-secondary mb-4">
                    ← Back to List
                </button>
                <h2 className="text-2xl font-bold mb-6">📅 Record Leave</h2>

                <form onSubmit={handleAddLeave}>
                    <div className="form-group">
                        <label className="form-label">Employee *</label>
                        <select
                            className="form-select"
                            value={leaveFormData.employeeId}
                            onChange={(e) => setLeaveFormData({ ...leaveFormData, employeeId: e.target.value })}
                            required
                        >
                            <option value="">Select employee...</option>
                            {employees.map(emp => (
                                <option key={emp.id} value={emp.id}>{emp.name} - {emp.position}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Leave Type *</label>
                        <select
                            className="form-select"
                            value={leaveFormData.leaveType}
                            onChange={(e) => setLeaveFormData({ ...leaveFormData, leaveType: e.target.value })}
                            required
                        >
                            <option value="annual">📅 Annual Leave</option>
                            <option value="sick">🏥 Sick Leave</option>
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="form-group">
                            <label className="form-label">Start Date *</label>
                            <input
                                type="date"
                                className="form-input"
                                value={leaveFormData.startDate}
                                onChange={(e) => setLeaveFormData({ ...leaveFormData, startDate: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">End Date *</label>
                            <input
                                type="date"
                                className="form-input"
                                value={leaveFormData.endDate}
                                onChange={(e) => setLeaveFormData({ ...leaveFormData, endDate: e.target.value })}
                                min={leaveFormData.startDate}
                                required
                            />
                        </div>
                    </div>

                    {leaveFormData.startDate && leaveFormData.endDate && (
                        <div className="mb-4 p-3" style={{
                            background: 'rgba(59,130,246,0.1)',
                            borderRadius: '6px',
                            border: '1px solid rgba(59,130,246,0.3)'
                        }}>
                            <p className="text-sm font-bold">Total Days: {days}</p>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Reason</label>
                        <textarea
                            className="form-input"
                            value={leaveFormData.reason}
                            onChange={(e) => setLeaveFormData({ ...leaveFormData, reason: e.target.value })}
                            placeholder="Reason for leave..."
                            rows="3"
                        />
                    </div>

                    {leaveStats && (
                        <div className="mb-4 p-3" style={{
                            background: 'rgba(245,158,11,0.1)',
                            borderRadius: '6px',
                            border: '1px solid rgba(245,158,11,0.3)'
                        }}>
                            <p className="text-sm font-bold mb-2">Remaining Leave After:</p>
                            <div className="grid grid-cols-2 gap-2">
                                <p className="text-xs">
                                    📅 Annual: {leaveFormData.leaveType === 'annual' ? leaveStats.annualRemaining - days : leaveStats.annualRemaining}/{leaveStats.annualTotal}
                                </p>
                                <p className="text-xs">
                                    🏥 Sick: {leaveFormData.leaveType === 'sick' ? leaveStats.sickRemaining - days : leaveStats.sickRemaining}/{leaveStats.sickTotal}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 mt-6">
                        <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                            ✅ Record Leave
                        </button>
                        <button type="button" onClick={() => setView('list')} className="btn btn-secondary">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    // Pay Salary Form
    if (view === 'paySalary') {
        const employee = employees.find(e => e.id === salaryFormData.employeeId);

        return (
            <div className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <button onClick={() => setView('list')} className="btn btn-secondary mb-4">
                    ← Back to List
                </button>
                <h2 className="text-2xl font-bold mb-6">💰 Pay Salary</h2>

                <form onSubmit={handleRecordSalary}>
                    <div className="form-group">
                        <label className="form-label">Employee *</label>
                        <select
                            className="form-select"
                            value={salaryFormData.employeeId}
                            onChange={(e) => setSalaryFormData({ ...salaryFormData, employeeId: e.target.value })}
                            required
                        >
                            <option value="">Select employee...</option>
                            {employees.map(emp => (
                                <option key={emp.id} value={emp.id}>{emp.name} - {emp.position}</option>
                            ))}
                        </select>
                    </div>

                    {employee && (
                        <div className="mb-4 p-3" style={{
                            background: 'rgba(16,185,129,0.1)',
                            borderRadius: '6px',
                            border: '1px solid rgba(16,185,129,0.3)'
                        }}>
                            <p className="text-sm text-gray mb-1">Salary Amount</p>
                            <p className="text-xl font-bold text-green">{formatCurrency(employee.monthlySalary)}</p>
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">Month *</label>
                        <input
                            type="month"
                            className="form-input"
                            value={salaryFormData.month}
                            onChange={(e) => setSalaryFormData({ ...salaryFormData, month: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">Pay From Bank *</label>
                        <select
                            className="form-select"
                            value={salaryFormData.bankId}
                            onChange={(e) => setSalaryFormData({ ...salaryFormData, bankId: e.target.value })}
                            required
                            disabled={salaryFormData.alreadyRecorded}
                        >
                            {BANKS.map(bank => (
                                <option key={bank.id} value={bank.id}>{bank.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="flex items-center" style={{ cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={salaryFormData.alreadyRecorded}
                                onChange={(e) => setSalaryFormData({ ...salaryFormData, alreadyRecorded: e.target.checked })}
                                style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span className="text-sm">Already recorded as expense transaction</span>
                        </label>
                        <p className="text-xs text-muted mt-1">
                            Check this if you've already added this salary as an expense. This will only record the payment without creating a new transaction or updating bank balance.
                        </p>
                    </div>

                    <div className="flex gap-3 mt-6">
                        <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                            ✅ Pay Salary
                        </button>
                        <button type="button" onClick={() => setView('list')} className="btn btn-secondary">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    // Employee Profile View
    if (view === 'profile' && selectedEmployee) {
        const leaveStats = getLeaveStats(selectedEmployee.id);
        const employeeLeaves = leaveRecords
            .filter(l => l.employeeId === selectedEmployee.id)
            .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        const employeeSalaries = salaryPayments
            .filter(p => p.employeeId === selectedEmployee.id)
            .sort((a, b) => b.month.localeCompare(a.month));

        return (
            <div className="fade-in">
                <button onClick={() => setView('list')} className="btn btn-secondary mb-4">
                    ← Back to List
                </button>

                <div className="card mb-4">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-yellow mb-1">{selectedEmployee.name}</h2>
                            <p className="text-lg text-gray">{selectedEmployee.position}</p>
                            <p className="text-sm text-muted">Joined: {new Date(selectedEmployee.joinDate).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => handleEditEmployee(selectedEmployee)}
                                className="btn btn-secondary"
                            >
                                ✏️ Edit
                            </button>
                            <button
                                onClick={() => handleDeleteEmployee(selectedEmployee)}
                                className="btn btn-danger"
                                title="Delete employee"
                            >
                                🗑️ Delete
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <p className="text-xs text-gray">💰 Monthly Salary</p>
                            <p className="text-xl font-bold text-green">{formatCurrency(selectedEmployee.monthlySalary)}</p>
                        </div>
                        {selectedEmployee.email && (
                            <div>
                                <p className="text-xs text-gray">📧 Email</p>
                                <p className="text-sm">{selectedEmployee.email}</p>
                            </div>
                        )}
                    </div>
                    {selectedEmployee.phone && (
                        <div className="mb-4">
                            <p className="text-xs text-gray">📱 Phone</p>
                            <p className="text-sm">{selectedEmployee.phone}</p>
                        </div>
                    )}
                </div>

                {/* Leave Summary */}
                <div className="card mb-4">
                    <h3 className="text-xl font-bold mb-4 text-yellow">📅 Leave Summary</h3>

                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <span className="text-sm">Annual Leave</span>
                            <span className="text-sm font-bold">{leaveStats.annualUsed}/{leaveStats.annualTotal} used ({leaveStats.annualRemaining} left)</span>
                        </div>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{
                                    width: `${(leaveStats.annualUsed / leaveStats.annualTotal) * 100}%`,
                                    background: 'rgba(59,130,246,0.8)'
                                }}
                            ></div>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div className="flex justify-between mb-2">
                            <span className="text-sm">Sick Leave</span>
                            <span className="text-sm font-bold">{leaveStats.sickUsed}/{leaveStats.sickTotal} used ({leaveStats.sickRemaining} left)</span>
                        </div>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{
                                    width: `${(leaveStats.sickUsed / leaveStats.sickTotal) * 100}%`,
                                    background: 'rgba(239,68,68,0.8)'
                                }}
                            ></div>
                        </div>
                    </div>

                    {employeeLeaves.length > 0 && (
                        <>
                            <h4 className="text-sm font-bold mb-2 text-gray">Recent Leave:</h4>
                            <div className="space-y-2">
                                {employeeLeaves.slice(0, 5).map(leave => (
                                    <div key={leave.id} className="p-2" style={{
                                        background: 'var(--secondary-bg)',
                                        borderRadius: '6px',
                                        border: '1px solid var(--border)'
                                    }}>
                                        <div className="flex justify-between">
                                            <span className="text-sm">
                                                {leave.leaveType === 'annual' ? '📅' : '🏥'} {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                                            </span>
                                            <span className="text-sm font-bold">{leave.days} days</span>
                                        </div>
                                        {leave.reason && <p className="text-xs text-muted mt-1">{leave.reason}</p>}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Salary History */}
                <div className="card">
                    <h3 className="text-xl font-bold mb-4 text-yellow">💵 Salary History</h3>
                    {employeeSalaries.length === 0 ? (
                        <p className="text-muted text-sm">No salary payments recorded yet</p>
                    ) : (
                        <div className="space-y-2">
                            {employeeSalaries.slice(0, 6).map(payment => (
                                <div key={payment.id} className="p-2" style={{
                                    background: payment.status === 'paid' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                                    borderRadius: '6px',
                                    border: `1px solid ${payment.status === 'paid' ? 'rgba(16,185,129,0.3)' : 'rgba(245,158,11,0.3)'}`
                                }}>
                                    <div className="flex justify-between">
                                        <span className="text-sm">
                                            {payment.status === 'paid' ? '✅' : '⏳'} {payment.month}
                                        </span>
                                        <span className="text-sm font-bold">{formatCurrency(payment.amount)}</span>
                                    </div>
                                    {payment.paidDate && (
                                        <p className="text-xs text-muted mt-1">Paid on {new Date(payment.paidDate).toLocaleDateString()}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return null;
};

export default Employees;
